import { Grid, Paper, TextField, Typography, CircularProgress, Box } from '@mui/material'
import { inject, observer } from 'mobx-react'
import * as React from 'react'
import Button from '../common/Button/Button'
import { IStores } from '../Stores'
import logo from '../OBXIcon.svg'

interface IMagicLinkRequestProps {
  isLoadingMagicLink?: boolean
  isPollingMagicLink?: boolean
  requestMagicLink?: (email: string) => Promise<{ success: boolean; sessionId?: string }>
  stopPolling?: () => void
  routing?: any
}

@inject((stores: IStores) => ({
  isLoadingMagicLink: stores.login.isLoadingMagicLink,
  isPollingMagicLink: stores.login.isPollingMagicLink,
  requestMagicLink: stores.login.requestMagicLink,
  stopPolling: stores.login.stopPolling,
  routing: stores.routing,
}))
@observer
class MagicLinkRequest extends React.Component<
  IMagicLinkRequestProps,
  { email: string }
> {
  public state = {
    email: '',
  }

  public componentDidMount() {
    const lastEmail = localStorage.getItem('lastLoginEmail')
    if (lastEmail) {
      this.setState({ email: lastEmail })
    }
  }

  public componentWillUnmount() {
    if (this.props.stopPolling) {
      this.props.stopPolling()
    }
  }

  public handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const { requestMagicLink } = this.props
    const { email } = this.state

    if (requestMagicLink && email) {
      await requestMagicLink(email)
      localStorage.setItem('lastLoginEmail', email)
    }
  }

  public handleCancel = () => {
    this.setState({ email: '' })
    if (this.props.stopPolling) {
      this.props.stopPolling()
    }
  }

  public render() {
    const { isLoadingMagicLink, isPollingMagicLink } = this.props
    const { email } = this.state

    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100vw',
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          margin: 0,
          padding: 0,
          background: 'linear-gradient(135deg, #29348F 0%, #8bb84b 50%, #29348F 100%)',
        }}
      >
        <Box sx={{ maxWidth: '480px', width: '90%', px: 2 }}>
          <Paper
            sx={(theme) => ({
              maxWidth: '480px',
              width: '100%',
              background:
                theme.palette.mode === 'dark'
                  ? 'rgba(50, 50, 50, 0.3)'
                  : 'rgba(255, 255, 255, 0.5)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border:
                theme.palette.mode === 'dark'
                  ? '1px solid rgba(80, 80, 80, 0.4)'
                  : '1px solid rgba(255, 255, 255, 0.4)',
              boxShadow:
                theme.palette.mode === 'dark'
                  ? '0 8px 32px 0 rgba(0, 0, 0, 0.5)'
                  : '0 8px 32px 0 rgba(0, 0, 0, 0.15)',
            })}
          >
            <Grid
              alignItems="center"
              container
              direction="column"
              justifyContent="center"
              sx={{
                padding: { xs: '40px 28px', sm: '48px 32px' },
                maxWidth: '480px',
              }}
            >
              <img
                src={logo}
                alt="Objective Surgical"
                width="140"
                style={{
                  maxWidth: '100%',
                  filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))',
                }}
              />
              <Grid
                container
                spacing={3}
                justifyContent="center"
                style={{ paddingTop: '28px', maxWidth: '420px' }}
              >
                <Grid item xs={12}>
                  <Typography
                    variant="h6"
                    align="center"
                    sx={(theme) => ({
                      mb: 1,
                      color:
                        theme.palette.mode === 'dark' ? 'white' : 'rgba(0, 0, 0, 0.87)',
                      fontWeight: 600,
                    })}
                  >
                    Sign in with Magic Link
                  </Typography>
                </Grid>

                {isPollingMagicLink ? (
                  <>
                    <Grid item xs={12}>
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        gap={2}
                      >
                        <CircularProgress
                          size={60}
                          sx={(theme) => ({
                            color:
                              theme.palette.mode === 'dark'
                                ? 'white'
                                : 'rgba(0, 0, 0, 0.87)',
                          })}
                        />
                        <Typography
                          variant="body1"
                          align="center"
                          sx={(theme) => ({
                            color:
                              theme.palette.mode === 'dark'
                                ? 'white'
                                : 'rgba(0, 0, 0, 0.87)',
                            fontWeight: 500,
                          })}
                        >
                          Waiting for you to click the link...
                        </Typography>
                        <Typography
                          variant="body2"
                          align="center"
                          sx={(theme) => ({
                            color:
                              theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.9)'
                                : 'rgba(0, 0, 0, 0.6)',
                          })}
                        >
                          Check your email on any device and click the magic link. This
                          page will automatically log you in!
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sx={{ textAlign: 'center' }}>
                      <Button
                        onClick={this.handleCancel}
                        sx={(theme) => ({
                          color:
                            theme.palette.mode === 'dark'
                              ? 'white'
                              : 'rgba(0, 0, 0, 0.87)',
                          '&:hover': {
                            backgroundColor:
                              theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.1)'
                                : 'rgba(0, 0, 0, 0.05)',
                          },
                        })}
                      >
                        Cancel
                      </Button>
                    </Grid>
                  </>
                ) : (
                  <Grid item xs={12}>
                    <form onSubmit={this.handleSubmit}>
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Email Address"
                            type="email"
                            value={email}
                            onChange={(e) => this.setState({ email: e.target.value })}
                            required
                            autoFocus
                            InputLabelProps={{
                              shrink: true,
                            }}
                            sx={(theme) => ({
                              '& .MuiInputLabel-root': {
                                color:
                                  theme.palette.mode === 'dark'
                                    ? 'rgba(255, 255, 255, 0.9)'
                                    : 'rgba(0, 0, 0, 0.6)',
                              },
                              '& .MuiOutlinedInput-root': {
                                color:
                                  theme.palette.mode === 'dark'
                                    ? 'white'
                                    : 'rgba(0, 0, 0, 0.87)',
                                '& fieldset': {
                                  borderColor:
                                    theme.palette.mode === 'dark'
                                      ? 'rgba(255, 255, 255, 0.3)'
                                      : 'rgba(0, 0, 0, 0.23)',
                                },
                                '&:hover fieldset': {
                                  borderColor:
                                    theme.palette.mode === 'dark'
                                      ? 'rgba(255, 255, 255, 0.5)'
                                      : 'rgba(0, 0, 0, 0.4)',
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor:
                                    theme.palette.mode === 'dark'
                                      ? 'rgba(255, 255, 255, 0.8)'
                                      : 'primary.main',
                                },
                                '& input': {
                                  color:
                                    theme.palette.mode === 'dark'
                                      ? 'white'
                                      : 'rgba(0, 0, 0, 0.87)',
                                },
                              },
                            })}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <Button
                            color="primary"
                            fullWidth
                            isLoading={isLoadingMagicLink}
                            type="submit"
                            variant="contained"
                            disabled={!email}
                          >
                            Send Magic Link
                          </Button>
                        </Grid>
                      </Grid>
                    </form>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </Box>
    )
  }
}

export default MagicLinkRequest
